import { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { BsStars } from 'react-icons/bs';

import { groupSteps } from '../../utils/stepGroups';

import PlanBlock from './PlanBlock';
import ProfileChips from './ProfileChips';
import ResultsBlock from './ResultsBlock';
import SaveBlock from './SaveBlock';
import StepGroupCard from './StepGroupCard';

const Avatar = () => (
	<div className="mt-0.5 grid h-7 w-7 flex-shrink-0 place-items-center rounded-control bg-primary-500 text-white">
		<BsStars className="h-3.5 w-3.5" />
	</div>
);

/**
 * The conversation: user turns right, assistant turns left with their tool steps and blocks.
 */
const MessageList = ({ conversation, preview, thinking, blocks }) => {
	const bodyRef = useRef(null);
	const count = conversation.turns.length;
	useEffect(() => {
		if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
	}, [count, thinking, conversation.lastSeq]);

	const renderBlock = (block) => {
		switch (block.type) {
			case 'profile':
				return (
					<ProfileChips
						key={`${block.type}-${block.seq}`}
						profile={conversation.profile}
						mandate={conversation.mandate}
					/>
				);
			case 'plan': {
				const { active, stale } = blocks.planState(block);
				return (
					<PlanBlock
						key={`${block.type}-${block.seq}`}
						plan={block}
						widens={block.widens}
						active={active}
						stale={stale}
						starting={blocks.starting}
						onStart={(includeUnchecked) => blocks.onStart(block, includeUnchecked)}
						onOpenForm={blocks.onOpenForm}
					/>
				);
			}
			case 'results':
				return (
					<div key={`${block.type}-${block.seq}`} className="space-y-3">
						<ResultsBlock result={block} preview={preview} />
						{blocks.isLatestResult(block) && (
							<SaveBlock
								defaultName={block.defaultName ?? 'Neue Suche'}
								saved={conversation.saved}
								saving={blocks.saving}
								onSave={blocks.onSave}
							/>
						)}
					</div>
				);
			case 'saved':
				return null;
			case 'error':
				return (
					<p
						key={`${block.type}-${block.seq}`}
						className="rounded-card border border-warning-500/40 bg-warning-200 px-3 py-2 text-sm text-warning-700"
					>
						{block.text}
					</p>
				);
			default:
				return null;
		}
	};

	return (
		<div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto">
			<div className="mx-auto max-w-[720px] space-y-6 px-5 py-6">
				{conversation.turns.map((turn) =>
					turn.role === 'user' ? (
						<div key={turn.seq} className="flex justify-end">
							<div className="max-w-[80%] whitespace-pre-wrap rounded-3xl rounded-br-lg bg-bgBase-400 px-4 py-2.5 text-md leading-6 text-text">
								{turn.text}
							</div>
						</div>
					) : (
						<div key={turn.seq} className="flex gap-3">
							<Avatar />
							<div className="min-w-0 flex-1 space-y-3">
								{turn.text && <p className="text-md leading-7 text-text">{turn.text}</p>}
								{turn.steps.length > 0 && (
									<div className="space-y-1.5">
										{groupSteps(turn.steps).map((group) => (
											<StepGroupCard key={group.id} group={group} />
										))}
									</div>
								)}
								{turn.blocks.map((block) => renderBlock(block))}
							</div>
						</div>
					),
				)}
				{thinking && (
					<div className="flex items-center gap-3">
						<Avatar />
						<span className="h-2 w-2 animate-pulse rounded-full bg-bgBase-700" />
					</div>
				)}
			</div>
		</div>
	);
};

MessageList.propTypes = {
	conversation: PropTypes.object.isRequired,
	preview: PropTypes.array,
	thinking: PropTypes.bool,
	blocks: PropTypes.shape({
		planState: PropTypes.func.isRequired,
		isLatestResult: PropTypes.func.isRequired,
		onStart: PropTypes.func.isRequired,
		onOpenForm: PropTypes.func.isRequired,
		onSave: PropTypes.func.isRequired,
		starting: PropTypes.bool,
		saving: PropTypes.bool,
	}).isRequired,
};

export default MessageList;
